import { useId } from "react";
import { Checkbox as BaseUICheckbox, CheckboxRootProps } from "@base-ui/react/checkbox";
import { CheckboxGroup as BaseUICheckboxGroup, CheckboxGroupProps as BaseUICheckboxGroupProps } from "@base-ui/react/checkbox-group";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import Link from "next/link";
import Label from "../label";
import "./style.scss";

interface CheckboxProps extends CheckboxRootProps {
	label?: React.ReactNode;
	required?: boolean;
}

function renderCheckboxLabel(label: React.ReactNode): React.ReactNode {
	if (typeof label !== "string") return label;

	const linkRegex = /<a\s+href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi;
	const nodes: React.ReactNode[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null = null;

	while ((match = linkRegex.exec(label)) !== null) {
		const [fullMatch, href, linkText] = match;

		if (match.index > lastIndex) {
			nodes.push(label.slice(lastIndex, match.index));
		}

		const safeHref = href?.trim();
		const isInternal = safeHref?.startsWith("/");

		if (safeHref) {
			if (isInternal) {
				nodes.push(
					<Link key={`${safeHref}-${match.index}`} href={safeHref} className="underline" onClick={(e) => e.stopPropagation()}>
						{linkText}
					</Link>,
				);
			} else {
				nodes.push(
					<a
						key={`${safeHref}-${match.index}`}
						href={safeHref}
						className="underline"
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => e.stopPropagation()}
					>
						{linkText}
					</a>,
				);
			}
		} else {
			nodes.push(fullMatch);
		}

		lastIndex = match.index + fullMatch.length;
	}

	if (lastIndex < label.length) {
		nodes.push(label.slice(lastIndex));
	}

	return nodes.length > 0 ? nodes : label;
}

export const Checkbox: React.FC<CheckboxProps> = ({ label, required, ...props }) => {
	return (
		<label className="flex items-center gap-2 text-base cursor-pointer group has-disabled:cursor-not-allowed has-data-disabled:cursor-not-allowed">
			<BaseUICheckbox.Root
				{...props}
				className="ms-checkbox p-0 transition border flex size-5 shrink-0 items-center justify-center rounded-sm data-unchecked:group-hover:outline-1 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:opacity-60"
			>
				<BaseUICheckbox.Indicator className="flex data-unchecked:hidden">
					<Check className="size-4" />
				</BaseUICheckbox.Indicator>
			</BaseUICheckbox.Root>

			<span>
				{renderCheckboxLabel(label)}
				{required && <span className="ml-1 text-(--ms-form-el-required-color,var(--ms-color-danger))">*</span>}
			</span>
		</label>
	);
};

interface CheckboxGroupProps extends BaseUICheckboxGroupProps {
	label?: string;
	required?: boolean;
	name?: string;
	checkBoxesContainerClassName?: string;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ children, label, required, checkBoxesContainerClassName, ...props }) => {
	const generatedId = useId();
	const id = props?.id ?? generatedId;

	return (
		<BaseUICheckboxGroup {...props} aria-labelledby={id} className={cn("flex flex-col items-start gap-2", props.className)}>
			{label && (
				<Label variant="legend" id={id} required={required}>
					{label}
				</Label>
			)}
			<div className={cn("flex flex-col gap-1 w-full", checkBoxesContainerClassName)}>{children}</div>
		</BaseUICheckboxGroup>
	);
};
